// Pure helpers for the Reports page (copy and encodings follow docs/design/mockups/_build/pages/15-reports.html).
import { timeZoneAbbreviation, zonedFormat } from '../../design/timeZone';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const PRESET_LABELS = {
  Custom: 'Custom dates',
  Today: 'Today',
  Yesterday: 'Yesterday',
  Last7Days: 'Last 7 days',
  Last30Days: 'Last 30 days',
  Last90Days: 'Last 90 days',
  ThisMonth: 'This month',
  PreviousMonth: 'Previous calendar month',
  ThisQuarter: 'This quarter',
  PreviousQuarter: 'Previous quarter',
  ThisYear: 'This year',
};

export const SEVERITY_ORDER = ['Immediate', 'Within48Hours', 'Standard', 'Informational'];
export const SEVERITY_LABELS = { Immediate: 'Immediate', Within48Hours: 'Within 48 Hours', Standard: 'Standard', Informational: 'Informational' };
export const SENTIMENT_LABELS = { VeryNegative: 'Very negative', Negative: 'Negative', Mixed: 'Mixed', Neutral: 'Neutral', Positive: 'Positive', VeryPositive: 'Very positive' };
export const LANGUAGE_LABELS = { 'en-US': 'English', 'es-US': 'Spanish' };
export const OUTCOME_LABELS = { Answered: 'Answered', Abandoned: 'Abandoned', Voicemail: 'Voicemail', Transferred: 'Transferred', Missed: 'Missed' };
export const DIRECTION_LABELS = { Inbound: 'Inbound', Outbound: 'Outbound', Internal: 'Internal' };

/** "Within 48 Hours or higher" when the list is a severity and everything above it, else the names. */
export function severityText(severities) {
  const list = (severities || []).filter((s) => SEVERITY_ORDER.includes(s));
  if (!list.length) return '';
  const lowest = Math.max(...list.map((s) => SEVERITY_ORDER.indexOf(s)));
  const isThreshold = list.length === lowest + 1 && lowest > 0 && lowest < SEVERITY_ORDER.length - 1;
  if (isThreshold) return `${SEVERITY_LABELS[SEVERITY_ORDER[lowest]]} or higher`;
  return SEVERITY_ORDER.filter((s) => list.includes(s)).map((s) => SEVERITY_LABELS[s]).join(', ');
}

const pad = (n) => String(n).padStart(2, '0');

export function clockText(hours, minutes) {
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${pad(minutes)} ${hours < 12 ? 'AM' : 'PM'}`;
}

const ordinal = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
};

/** Picker state from a 5-field cron the picker can produce; anything else is { frequency: 'custom' }. */
export function parseCron(cron) {
  const parts = String(cron || '').trim().split(/\s+/);
  const fallback = { frequency: 'custom', hour: 7, minute: 0, weekday: 1, monthDay: 1, cron: cron || '' };
  if (parts.length !== 5) return fallback;
  const [min, hour, dom, month, dow] = parts;
  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour) || month !== '*') return fallback;
  const base = { ...fallback, hour: Number(hour), minute: Number(min), cron };
  if (dom === '*' && dow === '*') return { ...base, frequency: 'daily' };
  if (dom === '*' && /^[0-6]$/.test(dow)) return { ...base, frequency: 'weekly', weekday: Number(dow) };
  if (/^\d+$/.test(dom) && dow === '*' && Number(dom) >= 1 && Number(dom) <= 28) return { ...base, frequency: 'monthly', monthDay: Number(dom) };
  return fallback;
}

export function buildCron({ frequency, hour, minute, weekday, monthDay, cron }) {
  if (frequency === 'daily') return `${minute} ${hour} * * *`;
  if (frequency === 'weekly') return `${minute} ${hour} * * ${weekday}`;
  if (frequency === 'monthly') return `${minute} ${hour} ${monthDay} * *`;
  return (cron || '').trim();
}

/** "Monthly, 1st at 7:00 AM", "Weekly, Monday 7:30 AM", "Daily, 5:30 PM"; other crons as written. */
export function describeCron(cron) {
  const p = parseCron(cron);
  const time = clockText(p.hour, p.minute);
  if (p.frequency === 'daily') return `Daily, ${time}`;
  if (p.frequency === 'weekly') return `Weekly, ${WEEKDAYS[p.weekday]} ${time}`;
  if (p.frequency === 'monthly') return `Monthly, ${ordinal(p.monthDay)} at ${time}`;
  return `Cron ${cron}`;
}

/** Schedule cadence for the saved-report list: "Scheduled monthly" or "Not scheduled". */
export function cadenceText(schedules) {
  if (!schedules || !schedules.length) return 'Not scheduled';
  const p = parseCron(schedules[0].cronExpression);
  return p.frequency === 'custom' ? 'Scheduled' : `Scheduled ${p.frequency}`;
}

export const groupKey = (group) => `${group.role}|${group.communityId || ''}|${group.hierarchyNodeId || ''}`;

/** The schedule dialog's "Other recipients" box: one address per line (commas and semicolons also split), blanks dropped. */
export const parseRecipients = (text) => (text || '').split(/[\n,;]+/).map((r) => r.trim()).filter(Boolean);

/** Shape check only; the API decides whether the address belongs to a Vertolytics user of the account. */
export const isEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');

/** "Dana Whitaker, Brevard managers (3)": people by name where known, groups with their current member count. */
export function recipientsText(schedule, directory) {
  const people = Object.fromEntries((directory?.people || []).map((p) => [p.email.toLowerCase(), p.displayName]));
  const groups = Object.fromEntries((directory?.groups || []).map((g) => [groupKey(g), g]));
  const list = [
    ...(schedule.recipients || []).map((email) => people[email.toLowerCase()] || email),
    ...(schedule.recipientGroups || []).map((g) => {
      const known = groups[groupKey(g)];
      return known ? `${known.name} (${known.memberCount})` : 'Recipient group';
    }),
  ];
  if (!list.length) return 'No recipients';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} and ${list.length - 2} more`;
}

/** Schedules in the mockup's order: the furthest next run first (paused last), then by name. */
export function sortSchedules(schedules) {
  const next = (s) => (s.enabled && s.nextRunAt ? new Date(s.nextRunAt).getTime() : -Infinity);
  return [...(schedules || [])].sort((a, b) => next(b) - next(a) || String(b.name).localeCompare(String(a.name)));
}

/** Calendar parts of an instant in the account time zone. */
export function zoneParts(value) {
  const date = value instanceof Date ? value : new Date(value);
  const parts = Object.fromEntries(
    zonedFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short', hourCycle: 'h23' })
      .formatToParts(date)
      .map((p) => [p.type, p.value])
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    weekday: parts.weekday,
  };
}

const SHORT_MONTHS = MONTHS.map((m) => m.slice(0, 3));
const dayKey = (p) => p.year * 10000 + p.month * 100 + p.day;
const daysBetween = (a, b) => Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86400000);

/** "Today", "Yesterday" or "Oct 1" in the account time zone. */
export function relativeDay(value, now = new Date()) {
  const p = zoneParts(value);
  const n = zoneParts(now);
  if (dayKey(p) === dayKey(n)) return 'Today';
  if (daysBetween(p, n) === 1) return 'Yesterday';
  return `${SHORT_MONTHS[p.month - 1]} ${p.day}`;
}

/** "Today 9:12 AM", "Yesterday", "Oct 1" for Recent exports. */
export function exportedWhen(value, now = new Date()) {
  const day = relativeDay(value, now);
  if (day !== 'Today') return day;
  const p = zoneParts(value);
  return `Today ${clockText(p.hour, p.minute)}`;
}

/** Next run: "Today 5:30 PM", "Mon, Oct 19, 7:30 AM" within a week, else "Nov 1, 7:00 AM". */
export function nextRunText(value, now = new Date()) {
  if (!value) return 'Not scheduled';
  const p = zoneParts(value);
  const n = zoneParts(now);
  const time = clockText(p.hour, p.minute);
  const days = daysBetween(n, p);
  if (days === 0) return `Today ${time}`;
  if (days > 0 && days < 7) return `${p.weekday}, ${SHORT_MONTHS[p.month - 1]} ${p.day}, ${time}`;
  return `${SHORT_MONTHS[p.month - 1]} ${p.day}, ${time}`;
}

/** Last-run badge of a schedule: DeliveryDisabled is "Rendered Oct 1, not sent" (kill switch). */
export function lastRunBadge(schedule, now = new Date()) {
  const when = schedule.lastRunAt ? relativeDay(schedule.lastRunAt, now) : null;
  const on = when === 'Today' || when === 'Yesterday' ? when.toLowerCase() : when;
  switch (schedule.lastRunOutcome) {
    case 'DeliveryDisabled':
      return { tone: 'warning', icon: 'power', text: `Rendered ${on}, not sent` };
    case 'Delivered':
      return { tone: 'good', icon: 'checkCircle', text: `Sent ${on}` };
    case 'Failed':
      return { tone: 'critical', icon: 'alert', text: `Failed ${on}` };
    default:
      return { tone: 'neutral', icon: null, text: 'Not run yet' };
  }
}

export function monthKey(year, month) {
  return `${year}-${pad(month)}`;
}

export function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

export function shortMonthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return `${SHORT_MONTHS[m - 1]} ${y}`;
}

export function shiftMonth(key, delta) {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return monthKey(d.getUTCFullYear(), d.getUTCMonth() + 1);
}

/** The month before the current month in the account time zone (the month that is invoiced). */
export function previousMonth(now = new Date()) {
  return shiftMonth(currentMonth(now), -1);
}

export function currentMonth(now = new Date()) {
  const p = zoneParts(now);
  return monthKey(p.year, p.month);
}

export function lastMonths(count, now = new Date()) {
  const first = previousMonth(now);
  return Array.from({ length: count }, (_, i) => shiftMonth(first, -i));
}

/** "Sep 1–30 ET" for a month key, with the account time zone. */
export function monthRangeText(key) {
  const [y, m] = key.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${SHORT_MONTHS[m - 1]} 1–${last} ${timeZoneAbbreviation()}`;
}

/** Calendar cells (Sunday first): { blank } for the days before the 1st, then the day counts. */
export function calendarCells(days) {
  if (!days || !days.length) return [];
  const [y, m, d] = days[0].date.split('-').map(Number);
  const lead = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return [
    ...Array.from({ length: lead }, (_, i) => ({ blank: true, key: `b${i}` })),
    ...days.map((day) => {
      const [yy, mm, dd] = day.date.split('-').map(Number);
      const dow = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay();
      return { key: day.date, day: dd, graph: day.graphCalls, stored: day.storedCalls, diff: day.graphCalls - day.storedCalls, weekend: dow === 0 || dow === 6, beforeHistory: Boolean(day.beforeHistory) };
    }),
  ];
}

export function formatPhone(value) {
  if (!value) return 'Unknown number';
  const digits = String(value).replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  return value;
}

export function pstnDirection(callType) {
  const t = String(callType || '').toLowerCase();
  if (/(^|_)in($|_)|inbound/.test(t)) return 'Inbound';
  if (/(^|_)out($|_)|outbound/.test(t)) return 'Outbound';
  return t ? t.replace(/_/g, ' ') : 'Unknown';
}

export function durationText(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

const SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Why a Graph PSTN call has no stored call record: a call record fetch that failed and is scheduled again
 * ("Retrying"), a call that ended less than a day before the reconciliation ran ("Will sync": Teams may not have
 * published it yet), or anything older ("Investigate", with the sync failure when one was recorded).
 */
/** Days (yyyy-MM-dd, account time zone) that start before the account's call history: Teams keeps call records for 30 days. */
export function beforeHistoryDays(days) {
  return new Set((days || []).filter((d) => d.beforeHistory).map((d) => d.date));
}

export function classifyDifference(row, reconciliation, historyGaps) {
  if (historyGaps && historyGaps.has(zoneDateKey(row.startDateTime || row.endDateTime)))
    return { status: 'Before history', tone: 'info', reason: "Before the account's call history; Teams keeps call records for 30 days" };
  if (row.syncRetryPending) return { status: 'Retrying', tone: 'info', reason: row.syncFailureReason || 'Call record sync failed; retry scheduled' };
  const generated = reconciliation?.generatedAt ? new Date(reconciliation.generatedAt).getTime() : Date.now();
  const end = row.endDateTime || row.startDateTime;
  const ended = new Date(end).getTime();
  if (generated - ended < SYNC_WINDOW_MS) {
    const afterUtcMidnight = String(end).slice(0, 10) !== zoneDateKey(end);
    return {
      status: 'Will sync',
      tone: 'info',
      reason: afterUtcMidnight ? 'Call ended after midnight UTC; Teams call record not yet published' : 'Ended just before the reconciliation; Teams call record not yet published',
    };
  }
  return { status: 'Investigate', tone: 'warning', reason: row.syncFailureReason || 'No stored call record has this call ID' };
}

const DIFFERENCE_RANK = { Investigate: 0, Retrying: 1, 'Will sync': 2, 'Before history': 3 };

/** Most important first: Investigate, then Retrying, Will sync and Before history; newest first within each. */
export function sortDifferences(differences) {
  const time = (d) => new Date(d.row.startDateTime).getTime();
  return [...differences].sort((a, b) => (DIFFERENCE_RANK[a.status] ?? 4) - (DIFFERENCE_RANK[b.status] ?? 4) || time(b) - time(a));
}

/** "21 of 33 are calls still in flight at month end and will sync on their own." (mockup wording) */
export function pstnBannerTail(willSync, total, reconciliation, month) {
  if (!total) return '; there are none this month.';
  const monthEnd = month ? new Date(`${shiftMonth(month, 1)}-01T00:00:00Z`).getTime() : null;
  const generated = reconciliation?.generatedAt ? new Date(reconciliation.generatedAt).getTime() : null;
  const when = monthEnd && generated && generated >= monthEnd ? 'at month end' : 'when the reconciliation ran';
  return `; ${willSync} of ${total.toLocaleString('en-US')} are calls still in flight ${when} and will sync on their own.`;
}

const zoneDateKey = (value) => {
  const p = zoneParts(value);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
};

/** "Sep 30, 11:58 PM" in the account time zone. */
export function shortDateTime(value) {
  const p = zoneParts(value);
  return `${SHORT_MONTHS[p.month - 1]} ${p.day}, ${clockText(p.hour, p.minute)}`;
}

const DURATION_RE = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/;

/** Preview cell text by column type (values are already in the account time zone). */
export function previewCell(value, type) {
  if (value == null || value === '') return '—';
  if (type === 'DateTime' && typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
    if (!m) return value;
    const weekday = WEEKDAYS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()].slice(0, 3);
    const date = `${SHORT_MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
    return m[4] ? `${weekday}, ${date} ${clockText(Number(m[4]), Number(m[5]))}` : `${date}, ${m[1]}`;
  }
  if (type === 'Duration') {
    if (typeof value === 'number') return durationText(value);
    const m = String(value).match(DURATION_RE);
    if (m) return durationText(Number(m[2] || 0) * 86400 + Number(m[3]) * 3600 + Number(m[4]) * 60 + Number(m[5]));
  }
  if (type === 'Percentage' && typeof value === 'number') return `${value.toFixed(1)}%`;
  if (type === 'Boolean') return value ? 'Yes' : 'No';
  if ((type === 'Integer' || type === 'Decimal') && typeof value === 'number') return value.toLocaleString('en-US');
  return String(value);
}

const EXTENSION = { Csv: '.csv', Xlsx: '.xlsx', Pdf: '.pdf' };

export function exportExtension(record) {
  const match = String(record.fileName || '').match(/\.([a-z0-9]+)$/i);
  if (match) return `.${match[1].toLowerCase()}`;
  return EXTENSION[record.format] || '';
}

function dateRangeText(range) {
  if (!range) return '';
  if (range.preset === 'Custom' && range.from && range.to) {
    const [fy, fm, fd] = range.from.slice(0, 10).split('-').map(Number);
    const [ty, tm, td] = range.to.slice(0, 10).split('-').map(Number);
    const monthEnd = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
    if (fy === ty && fm === tm && fd === 1 && td === monthEnd) return `${SHORT_MONTHS[fm - 1]} ${fy}`;
    if (fy === ty && fm === tm) return `${SHORT_MONTHS[fm - 1]} ${fd}–${td}`;
    return `${SHORT_MONTHS[fm - 1]} ${fd} – ${SHORT_MONTHS[tm - 1]} ${td}`;
  }
  return PRESET_LABELS[range.preset] || '';
}

/** "Calls, Brevard, Sep 2026": what was exported, for whom, and when. */
export function recentExportTitle(record) {
  if (record.kind === 'CommunitySummary') return record.title;
  const scope = (record.filterSummary || '')
    .split(';')
    .map((part) => part.replace(/^[^:]+:\s*/, '').trim())
    .filter((part) => part && part !== 'None')
    .join(', ');
  const period = record.month ? shortMonthLabel(record.month) : dateRangeText(record.dateRange);
  const isTable = record.kind === 'Dataset' || record.kind === 'TranscriptArchive' || record.kind === 'SavedReport';
  return [record.title, scope || (isTable ? 'all' : ''), period].filter(Boolean).join(', ');
}

/** How to re-run a recent export: { action, ... } consumed by the Exports tab; null for the retired legacy routes and page tables. */
export function rerunRequest(record) {
  const format = (record.format || 'Csv').toLowerCase();
  switch (record.kind) {
    case 'SavedReport':
      return { action: 'saved', id: record.savedReportId, format };
    case 'CommunitySummary':
      return {
        action: 'community',
        params: { communityId: record.communityId, month: record.month, format, includeCallerNames: Boolean(record.includeCallerNames), sections: record.parameters?.sections || undefined },
      };
    case 'LegacyExport':
      return null;
    case 'PageExport': {
      const { route, ...params } = record.parameters || {};
      return route ? { action: 'page', route, params: { ...params, format } } : null;
    }
    case 'TranscriptArchive':
      return { action: 'transcripts', params: filterParams(record.filter, record.dateRange) };
    default:
      return {
        action: 'run',
        query: {
          dataset: record.dataset,
          columns: record.columns || [],
          filter: record.filter || {},
          dateRange: record.dateRange || { preset: 'Last30Days' },
          month: record.month || undefined,
          sortColumn: record.sortColumn || undefined,
          sortDescending: Boolean(record.sortDescending),
        },
        format,
      };
  }
}

const FILTER_PARAM_KEYS = {
  hierarchyNodeIds: 'hierarchyNodeIds',
  communityIds: 'communityIds',
  queueIds: 'queueIds',
  functions: 'functions',
  agentIds: 'agentIds',
  classificationCategoryIds: 'categoryIds',
  classificationSubcategoryIds: 'subcategoryIds',
  severities: 'severities',
  callerSentiments: 'callerSentiments',
  languages: 'languages',
  directions: 'directions',
  outcomes: 'outcomes',
};

/** AnalyticsFilter + ReportDateRange as export query parameters (contracts §7). */
export function filterParams(filter, dateRange) {
  const params = {};
  Object.entries(FILTER_PARAM_KEYS).forEach(([field, key]) => {
    const values = filter?.[field];
    if (Array.isArray(values) && values.length) params[key] = values;
  });
  if (filter?.searchText) params.search = filter.searchText;
  if (dateRange?.preset === 'Custom') {
    if (dateRange.from) params.from = dateRange.from.slice(0, 10);
    if (dateRange.to) params.to = dateRange.to.slice(0, 10);
  } else if (dateRange?.preset) {
    params.preset = dateRange.preset;
  }
  return params;
}

/** The global filter bar (hierarchy node or communities, from/to) as an AnalyticsFilter query body. */
export function barQuery(barParams) {
  const filter = {};
  if (barParams.communityIds) filter.communityIds = barParams.communityIds.split(',');
  else if (barParams.hierarchyNodeIds) filter.hierarchyNodeIds = barParams.hierarchyNodeIds.split(',');
  return { filter, dateRange: { preset: 'Custom', from: barParams.from, to: barParams.to } };
}

/* ---------- Builder columns ---------- */

const TOP_LEVEL = 'hierarchy:*';

// The approved mockup's column set and order per dataset (reports.html builder); the rest go under "More columns".
const MOCKUP_COLUMNS = {
  Calls: ['startTime', 'callerName', 'callerNumber', TOP_LEVEL, 'community', 'entryPoint', 'queue', 'agent', 'route', 'outcome', 'waitTime', 'talkTime', 'classifications', 'severity', 'callerSentiment', 'language', 'languageDetectionMethod', 'summary', 'actionItems', 'transcriptLink'],
};

/** { primary, more }: the mockup's columns in its order, then every other column in the dataset's own order. */
export function splitColumns(dataset, columns) {
  const order = MOCKUP_COLUMNS[dataset];
  if (!order) return { primary: columns, more: [] };
  const topLevel = columns.find((c) => c.key.startsWith('hierarchy:') && c.key !== 'hierarchyPath');
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const primary = order.map((key) => (key === TOP_LEVEL ? topLevel : byKey[key])).filter(Boolean);
  const chosen = new Set(primary.map((c) => c.key));
  return { primary, more: columns.filter((c) => !chosen.has(c.key)) };
}

/** Keys in picker order (primary, then more), so toggling keeps the preview columns in that order. */
export const pickerOrder = (dataset, columns) => {
  const { primary, more } = splitColumns(dataset, columns);
  return [...primary, ...more].map((c) => c.key);
};

/** Board tab picker: communities busiest first (GET reports/community-summary/communities), else by name. */
export function boardCommunityOptions(communities, counts) {
  if (!counts) return [...communities].sort((a, b) => a.name.localeCompare(b.name));
  const byId = Object.fromEntries(communities.map((c) => [c.id, c]));
  return counts.filter((c) => byId[c.communityId]).map((c) => ({ ...byId[c.communityId], callCount: c.callCount }));
}

/** The filter bar's single community when it has one, else the busiest community of the month. */
export function defaultBoardCommunity(options, filterCommunityIds) {
  const fromBar = filterCommunityIds && filterCommunityIds.length === 1 ? filterCommunityIds[0] : null;
  if (fromBar && options.some((c) => c.id === fromBar)) return fromBar;
  return options.length ? options[0].id : '';
}
